import {
  CandidateInterviewReminderState,
  Paths,
} from 'candidate_profile_redesign/shared/types';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import { Dispatch, State } from '../use_candidate_profile';

type InterviewReminderStatePayload = {
  reminder: CandidateInterviewReminderState | { error: Error };
  stageId: number | undefined;
};

export const setCandidateInterviewReminder =
  (_: State, dispatch: Dispatch) =>
  (payload: InterviewReminderStatePayload) => {
    dispatch({ type: 'setCandidateInterviewReminder', payload });
  };

type StringPathKeys = {
  [K in keyof Paths]-?: Paths[K] extends string ? K : never;
}[keyof Paths];

const apis: Record<string, [StringPathKeys, 'GET' | 'PUT' | 'DELETE']> = {
  cancel: ['cancelInterviewReminderPath', 'PUT'],
  set: ['setInterviewReminderPath', 'PUT'],
  get: ['getInterviewReminderPath', 'GET'],
};

const writeError =
  (fn: (state: State, dispatch: Dispatch, stageId?: number) => Promise<void>) =>
  (state: State, dispatch: Dispatch) =>
  async (stageId?: number) => {
    try {
      await fn(state, dispatch, stageId);
    } catch (e) {
      dispatch({
        type: 'setCandidateInterviewReminder',
        payload: {
          reminder: { error: e as Error },
          stageId,
        },
      });
    }
  };

const syncApi =
  (
    api: typeof apis[string],
    onSuccess?: (
      reminder: CandidateInterviewReminderState,
      state: State,
      dispatch: Dispatch
    ) => void
  ) =>
  async (state: State, dispatch: Dispatch, stageId?: number) => {
    const [pathKey, method] = api;

    const result = await standardRequestWithErrorFlash(state.paths[pathKey], {
      method,
    });

    dispatch({
      type: 'setCandidateInterviewReminder',
      payload: {
        reminder: result.data.reminder,
        stageId,
      },
    });

    onSuccess?.(result.data.reminder, state, dispatch);
  };

export const cancelInterviewReminder = writeError(syncApi(apis.cancel));

export const scheduleInterviewReminder = writeError(syncApi(apis.set));

export const getCandidateInterviewReminder =
  (state: State, dispatch: Dispatch) =>
  async (reminderGetPath: string, stageId: number) => {
    if (!reminderGetPath) {
      return;
    }

    const result = await standardRequestWithErrorFlash.get(reminderGetPath);

    dispatch({
      type: 'setCandidateInterviewReminder',
      payload: {
        reminder: result.data.reminder,
        stageId,
      },
    });
  };

export type Actions = {
  type: 'setCandidateInterviewReminder';
  payload: InterviewReminderStatePayload;
};
